// pages/UserList.tsx
import React, { useState, useEffect } from 'react';
import GenericTemplate from '../components/templates/GenericTemplate';
import { Avatar, Box, Button, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { useHistory } from 'react-router';

import { getXrossUserList } from '../admin/firebase/user';
import { XROSS_USER_PROFILE } from '../common/interface';
import { _ACTIVE_, _SUSPEND_, _UNSUBSCRIBE_ } from '../common/define';

// 汎用検索
import { SearchPanel } from '../components/SearchPanel';
import {
  SearchCondition as BaseCondition,
  SearchKeyOption,
  KeyValueOptionsMap,
} from '../common/search/types';

// 新APIに合わせ、modeを付けられる型をローカルで用意
type MatchMode = 'equals' | 'prefix';
type SearchCondition = BaseCondition & { mode?: MatchMode };

const useStyles = makeStyles({
  table: { minWidth: 650 },
  tableCell: { padding: '0px 8px' },
});

const PAGE_SIZE_DEFAULT = 100;

const searchKeys: SearchKeyOption[] = [
  { value: 'uid', label: 'UID' }, // equals
  { value: 'xrossUserName', label: 'ユーザ名' }, // prefix
  { value: 'email', label: 'メールアドレス' },
  { value: 'xrossUserStatus', label: 'ユーザステータス' },
  // { value: 'isAdmin', label: '管理者' },
];

const postStatusOptions: { value: string; label: string }[] = [
  { value: '_STORED_', label: '保存済み' },
  { value: '_TEMPORARILY_', label: '一時保存' },
  { value: '_DELETED_', label: '削除済み' },
];

const isAdminOptions = [
  { value: true, label: '管理者' },
  { value: false, label: '一般ユーザ' },
];

const xrossUserStatusOptions = [
  { value: _ACTIVE_, label: 'アクティブ' },
  { value: _SUSPEND_, label: '停止中' },
  { value: _UNSUBSCRIBE_, label: '退会' },
];

const keyValueOptions: KeyValueOptionsMap = {
  postStatus: postStatusOptions,
  isAdmin: isAdminOptions,
  xrossUserStatus: xrossUserStatusOptions,
};

const toDateStrings = (ms: number) => String(new Date(ms));

const withMode = (conds: BaseCondition[]): SearchCondition[] => {
  return conds
    .filter((c) => c.key)
    .map((c) => {
      if (
        c.key === 'xrossUserName' ||
        c.key === 'xrossUserId' ||
        c.key === 'email'
      ) {
        return { ...c, mode: 'prefix' as MatchMode }; // ← email を prefix に
      }
      if (c.key === 'uid' || c.key === 'postStatus' || c.key === 'isAdmin') {
        return { ...c, mode: 'equals' as MatchMode };
      }
      return { ...c, mode: 'equals' as MatchMode };
    });
};

const UserList: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();

  const [xrossUserList, setXrossUserList] = useState<XROSS_USER_PROFILE[]>([]);
  const [nextCursor, setNextCursor] = useState<any | null>(null); // ← ★ null開始
  const [isLoading, setIsLoading] = useState(false);

  // 検索状態
  const [conditions, setConditions] = useState<BaseCondition[]>([
    { key: '', value: '' },
  ]);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_DEFAULT);
  const [page, setPage] = useState<number>(0);
  const [lastPage, setLastPage] = useState<boolean>(false);

  // 検索トリガ
  const [searchNonce, setSearchNonce] = useState<string>('');

  // 検索実行（SearchPanel → ここ）
  const handleSearch = ({
    conditions: rawConds,
    pageSize,
  }: {
    conditions: BaseCondition[];
    pageSize: number;
  }) => {
    setConditions(rawConds);
    setPageSize(pageSize);
    setSearchNonce(Math.random().toString(36).slice(-8));
  };

  // クリア
  const handleClear = () => {
    setConditions([{ key: '', value: '' }]);
    setXrossUserList([]);
    setNextCursor(null); // ← ★ reset
    setPage(0);
    setLastPage(false);
    setPageSize(PAGE_SIZE_DEFAULT);
    setSearchNonce(Math.random().toString(36).slice(-8)); // 空検索
  };

  // 初回 & 再検索
  useEffect(() => {
    const fetch = async () => {
      if (isLoading) return;
      setIsLoading(true);

      const conds = withMode(conditions);
      const list = await getXrossUserList(pageSize, null, conds);

      setXrossUserList(list);
      setPage(0);
      setLastPage(list.length < pageSize);
      setNextCursor(list.length ? list[list.length - 1] : null); // ← ★ 最後の1件（id含む）をカーソルに
      setIsLoading(false);
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchNonce]);

  // ページング：次
  const handleNextPage = async () => {
    if (isLoading || lastPage) return;
    setIsLoading(true);

    const conds = withMode(conditions);
    const list = await getXrossUserList(pageSize, nextCursor, conds);

    // “そのページだけ”表示：必要なら concat に変更可
    setXrossUserList(list);
    setPage((p) => p + 1);
    setLastPage(list.length < pageSize);
    setNextCursor(list.length ? list[list.length - 1] : nextCursor);
    setIsLoading(false);
  };

  const handleClick = (id: string) => history.push('/UserDetail/' + id);

  return (
    <GenericTemplate title="ユーザリスト">
      {/* ▼ 汎用検索パネル */}
      <SearchPanel
        conditions={conditions}
        onChange={setConditions}
        onSearch={handleSearch}
        onClear={handleClear}
        searchKeys={searchKeys}
        keyValueOptions={keyValueOptions}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
      />

      <TableContainer component={Paper}>
        <Table className={classes.table} aria-label="user table">
          <TableHead>
            <TableRow>
              <TableCell>No.</TableCell>
              <TableCell>uid</TableCell>
              <TableCell>xross ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Avatar</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Profile</TableCell>
              <TableCell>Home Location Name</TableCell>
              <TableCell>既定の位置</TableCell>
              <TableCell>既定の公開範囲</TableCell>
              <TableCell>作成日時</TableCell>
              <TableCell>更新日時</TableCell>
              <TableCell>友達</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {xrossUserList.map((u, idx) => (
              <TableRow
                key={u.id ?? idx}
                hover
                onClick={() => handleClick(u.id)}
              >
                <TableCell>{page * pageSize + idx + 1}</TableCell>
                <TableCell>
                  <div>{u.id}</div>
                  <div>{u.email}</div>
                  {u.isAdmin && (
                    <Box
                      component="span"
                      style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        background: 'red',
                        color: 'white',
                        borderRadius: 8,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                      }}
                    >
                      Admin
                    </Box>
                  )}
                </TableCell>
                <TableCell>{u.xrossUserID}</TableCell>
                <TableCell>{u.xrossUserName}</TableCell>
                <TableCell>
                  <Avatar src={u.xrossAvatarURL} />
                </TableCell>
                <TableCell>{u.xrossUserStatus}</TableCell>
                <TableCell>{u.profile}</TableCell>
                <TableCell>{u.homeGLocationLabel}</TableCell>
                <TableCell>
                  {u.isDefaultLocationAtHome ? 'ホームを設定' : 'GPS位置'}
                </TableCell>
                <TableCell>{u.defaultPostDisclosureRange}</TableCell>
                <TableCell>
                  {u.createdAt?.seconds
                    ? toDateStrings(u.createdAt.seconds * 1000)
                    : ''}
                </TableCell>
                <TableCell>
                  {u.lastRefreshAt?.seconds
                    ? toDateStrings(u.lastRefreshAt.seconds * 1000)
                    : ''}
                </TableCell>
                <TableCell>{/* 友達数など */}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleNextPage}
          disabled={isLoading || lastPage}
        >
          次のLIST
        </Button>
        <Box ml={2} component="span">
          現在のページ: {page + 1}
        </Box>
      </Box>
    </GenericTemplate>
  );
};

export default UserList;
